import "./index.css";
import { clsx } from "clsx";
import type { ChangeEvent, DragEvent } from "react";
import { useCallback, useMemo, useRef } from "react";
import { useTranslations } from "../../i18n";
import { generateUniqueFilename, renameFile } from "../../utils/file";
import { Button } from "../Button/Button";
import { Divider } from "../Divider";
import { Spinner } from "../Spinner";
import { Text } from "../Text";
import { Attachment } from "./Attachment";
import { DEFAULT_MAX_FILE_SIZE } from "./constants";
import type { IFileUploaderProps } from "./types";

export const FileUploader = (props: IFileUploaderProps) => {
  const {
    files,
    className,
    maxFiles = 1,
    maxFileSize = DEFAULT_MAX_FILE_SIZE,
    accept,
    onChange,
    onError,
    showAttachments = true,
    disabled = false,
    loading = false,
    renameFiles = false,
  } = props;
  const { t } = useTranslations();

  const inputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!disabled) {
      const droppedFiles = Array.from(event.dataTransfer.files);
      addFiles(droppedFiles);
    }
  };

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      const selectedFiles = Array.from(event.target.files || []);
      addFiles(selectedFiles);

      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  const handleUploadFiles = useCallback(() => {
    if (!disabled) {
      inputRef.current?.click();
    }
  }, [disabled]);

  const handleRemoveFile = useCallback(
    (fileToRemove?: File) => {
      if (!fileToRemove) return;

      const fileId = `${fileToRemove.name}-${fileToRemove.size}-${fileToRemove.lastModified}`;
      const updatedFiles = files.filter((file) => {
        const currentFileId = `${file.name}-${file.size}-${file.lastModified}`;
        return currentFileId !== fileId;
      });
      onChange(updatedFiles);
    },
    [files, onChange],
  );

  const acceptedTypes = useMemo(() => {
    return accept ? accept.split(",").map((type) => type.trim()) : null;
  }, [accept]);

  const addFiles = useCallback(
    (newFiles: File[]) => {
      const rejectedFiles: string[] = [];
      const oversizedFiles: string[] = [];
      const duplicateFiles: string[] = [];

      // For single file mode, don't check for duplicates since we replace
      const existingFileIds =
        maxFiles === 1
          ? new Set()
          : new Set(files.map((f) => `${f.name}-${f.size}-${f.lastModified}`));

      // For single file mode, we can always accept 1 file (replacing existing)
      const availableSlots =
        maxFiles === 1
          ? 1
          : maxFiles
            ? Math.max(0, maxFiles - files.length)
            : Infinity;
      let processedCount = 0;

      const validFiles = newFiles.filter((file) => {
        // Check if we've reached the limit
        if (processedCount >= availableSlots) {
          return false;
        }

        // Check for duplicates
        const fileId = `${file.name}-${file.size}-${file.lastModified}`;
        if (existingFileIds.has(fileId)) {
          duplicateFiles.push(file.name);
          return false;
        }

        // Check file size
        if (maxFileSize && file.size > maxFileSize) {
          oversizedFiles.push(file.name);
          return false;
        }

        // Check file type
        if (acceptedTypes) {
          const isValidType = acceptedTypes.some((type) => {
            if (type.startsWith(".")) {
              return file.name.toLowerCase().endsWith(type.toLowerCase());
            } else {
              return file.type === type;
            }
          });

          if (!isValidType) {
            rejectedFiles.push(file.name);
            return false;
          }
        }

        processedCount++;
        return true;
      });

      // Report errors
      if (
        rejectedFiles.length > 0 ||
        oversizedFiles.length > 0 ||
        duplicateFiles.length > 0
      ) {
        const errors: string[] = [];
        if (rejectedFiles.length > 0) {
          errors.push(
            `${rejectedFiles.length} file(s) rejected due to invalid type`,
          );
        }
        if (oversizedFiles.length > 0) {
          errors.push(
            `${oversizedFiles.length} file(s) rejected due to size limit`,
          );
        }
        if (duplicateFiles.length > 0) {
          errors.push(`${duplicateFiles.length} duplicate file(s) skipped`);
        }
        onError?.(errors.join(". "));
      }

      if (validFiles.length > 0) {
        const processedFiles = renameFiles
          ? validFiles.map((file, index) => {
              const uniqueName = generateUniqueFilename(file, index);
              return renameFile(file, uniqueName);
            })
          : validFiles;

        if (maxFiles === 1) {
          onChange(processedFiles);
        } else {
          onChange([...files, ...processedFiles]);
        }
      }
    },
    [
      files,
      maxFiles,
      maxFileSize,
      acceptedTypes,
      onChange,
      onError,
      renameFiles,
    ],
  );

  const rootClass = clsx(
    "nuco-file-uploader",
    disabled && "nuco-file-uploader--disabled",
    className,
  );
  const buttonText =
    maxFiles === 1
      ? t("ui.fileUploader.chooseFile")
      : t("ui.fileUploader.chooseFiles");

  const attachmentsJSX = useMemo(() => {
    return files.map((file: File, i: number) => {
      const fileId = `${file.name}-${file.size}-${file.lastModified}_${i}`;
      return (
        <Attachment key={fileId} file={file} onRemove={handleRemoveFile} />
      );
    });
  }, [files, handleRemoveFile]);

  return (
    <div className={rootClass}>
      <div
        className="nuco-file-uploader__dropzone"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <input
          ref={inputRef}
          className="nuco-file-uploader__input"
          type="file"
          accept={accept}
          multiple={maxFiles > 1}
          onChange={handleFileSelect}
          disabled={disabled}
        />

        {showAttachments && files.length > 0 && (
          <>
            <div className="nuco-file-uploader__files">{attachmentsJSX}</div>
            <Divider />
          </>
        )}

        <div className="nuco-file-uploader__action-row">
          <Button
            size="sm"
            variant="soft"
            color="neutral"
            onClick={handleUploadFiles}
            disabled={disabled}
            content={buttonText}
          />
          <Text size="sm" className="nuco-file-uploader__hint">
            {t("ui.fileUploader.dragAndDrop")}
          </Text>
          {loading && (
            <div className="nuco-file-uploader__spinner">
              <Spinner color="neutral" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
